import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Card,
  Typography,
  Container,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  TextField,
  FormGroup,
  Chip,
  Stack,
  useTheme
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { InfinitySpin } from "react-loader-spinner";
import Lottie from "lottie-react";
import WELL_DONE from '../../well-done.json';
import * as Yup from 'yup';
import axios from "axios";
import CustomSubmitButton from "../../Components/CustomSubmitButton/CustomSubmitButton";
import CustomTextField from "../../Components/CustomTextField/CustomTextField";

const SurveyForm = () => {
  const { survey_token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isVerify, setIsVerify] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isServiceListDetails, setServiceListDetails] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [formConfig, setFormConfig] = useState({
    questions: []
  });
  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState(Yup.object({}));
  const [formReady, setFormReady] = useState(false);
  useEffect(() => {
    getServicesList(survey_token);
  }, [survey_token]);

  useEffect(() => {
    if (serviceList?.length > 0) {
      loadFormConfig();
    }
  }, [serviceList]);

  const getServicesList = async (survey_token) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "https://api.nopapertimesheet.com/api/lead/get-details",
        {
          params: { uuid: survey_token },
        }
      );
      if (response && response?.data?.s === 0) {
        navigate("/");
        return;
      }
      if (response && response?.data?.s) {
        const questionAnswer = JSON.parse(response?.data?.r?.question_answer || '{}');
        const questions = questionAnswer?.questions || [];

        if (response?.data?.r?.lead_status === 1) {
          setIsVerify(true);
        }
        setServiceList(questions);
        setServiceListDetails(response?.data?.r);
        return questions;
      } else {
        setServiceList([]);
        return [];
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Failed to load survey. Please try again.");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const buildValidationSchema = (questions) => {
    const schema = {};
    questions.forEach(question => {
      if (question.required) {
        if (question.type === 'checkbox' || question.type === 'multi_select') {
          schema[question.id] = Yup.array()
            .min(1, `${question.label} is required`)
            .required(`${question.label} is required`);
        } else if (question.type === 'date') {
          schema[question.id] = Yup.date()
            .required(`${question.label} is required`)
            .typeError('Please enter a valid date');
        } else if (question.type === 'number') {
          schema[question.id] = Yup.number()
            .required(`${question.label} is required`)
            .typeError('Please enter a valid number');
        } else {
          schema[question.id] = Yup.string()
            .required(`${question.label} is required`);
        }
      }
    });
    return Yup.object().shape(schema);
  };

  const loadFormConfig = async () => {
    setIsLoading(true);
    try {
      const formConfig = {
        title: "Survey Form",
        description: "Please fill out this survey form",
        questions: serviceList
      };
      setFormConfig(formConfig);

      const initialVals = {};
      serviceList.forEach(question => {
        if (question?.type === 'checkbox' || question.type === 'multi_select') {
          initialVals[question.id] = [];
        } else if (question.type === 'number') {
          initialVals[question.id] = question.min || '';
        } else if (question.type === 'date') {
          initialVals[question.id] = null;
        } else {
          initialVals[question.id] = '';
        }
      });

      setInitialValues(initialVals);
      setValidationSchema(buildValidationSchema(serviceList));
      setFormReady(true); // trigger formik re-init
    } catch (error) {
      console.log("error", error);
      toast.error("Failed to load survey. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const formData = new FormData();
        const questionAnswer = {
          form_id: "",
          title: formConfig?.title || "",
          description: formConfig?.description || "",
          questions: formConfig?.questions?.map(question => {
            const baseQuestion = {
              id: question.id,
              type: question.type,
              label: question.label,
              required: question.required || false
            };
            if (question.options) {
              baseQuestion.options = question.options;
            }
            baseQuestion.answer = values[question.id];
            return baseQuestion;
          }) || []
        };
        formData.append("lead_id", isServiceListDetails?.id);
        formData.append("question_answer", JSON.stringify(questionAnswer));
        formData.append("lead_status", 1);
        const getDetailsTimesheet = isServiceListDetails?.caregiver_details;
        const token = getDetailsTimesheet?.token;
        const apiKey = getDetailsTimesheet?.apikey;
        const response = await axios.post(
          "https://api.nopapertimesheet.com/api/lead/update-status",
          formData,
          {
            headers: {
              token: token,
              apikey: apiKey,
            },
          }
        );

        if (response && response?.data?.s) {
          setIsSubmitted(true);
          toast.success("Survey submitted successfully!");
          resetForm();
        } else {
          toast.error("Submission failed. Please try again.");
        }
      } catch (error) {
        console.log("error", error);
        toast.error("An error occurred. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const renderQuestionField = (question, index) => {
    const questionNumber = `Q${index + 1}`;

    switch (question?.type) {
      case 'text':
      case 'email':
        return (
          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <CustomTextField
              fullWidth
              id={question?.id}
              name={question?.id}
              type={question?.type}
              value={formik?.values[question?.id] || ''}
              onBlur={formik.handleBlur}
              error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
              helperText={formik.touched[question?.id] && formik.errors[question?.id]}
              placeholder={question?.placeholder}
              required={question.required}
              onChange={(e) => {
                const { value } = e.target;
                if (value.trimStart() !== value) {
                  e.target.value = value.trimStart();
                }
                formik.handleChange(e);
              }}
              sx={{
                borderRadius: '15px',
              }}
            />
          </Box>
        );
      case 'textarea':
        return (
          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <TextField
              fullWidth
              id={question.id}
              name={question.id}
              multiline
              rows={3}
              value={formik.values[question.id] || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched[question.id] && Boolean(formik.errors[question.id])}
              helperText={formik.touched[question.id] && formik.errors[question.id]}
              required={question.required}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Box>
        );
      case 'radio':
        return (
          <>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <FormControl
              component="fieldset"
              fullWidth
              error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
            >
              <RadioGroup
                name={question.id}
                value={formik?.values[question?.id] || ''}
                onChange={formik.handleChange}
                sx={{
                  '& .MuiFormControlLabel-root': {
                    marginLeft: '-9px'
                  }
                }}
              >
                {question?.options?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontFamily: 'Outfit', fontWeight: 400 }}>{option}</Typography>}
                  />
                ))}
              </RadioGroup>
              {formik.touched[question?.id] && formik.errors[question?.id] && (
                <Typography color="error" variant="caption">
                  {formik.errors[question?.id]}
                </Typography>
              )}
            </FormControl>
          </>
        );
      case 'checkbox':
        return (
          <>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <FormControl
              component="fieldset"
              fullWidth
              error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
            >
              <FormGroup>
                {question.options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        size="small"
                        checked={formik.values[question.id]?.includes(option) || false}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? [...(formik.values[question.id] || []), option]
                            : (formik.values[question.id] || []).filter(v => v !== option);
                          formik.setFieldValue(question.id, newValue);
                        }}
                        name={`${question.id}.${option}`}
                      />
                    }
                    label={<Typography sx={{ fontFamily: 'Outfit', fontWeight: 400 }}>{option}</Typography>}
                  />
                ))}
              </FormGroup>
              {formik.touched[question?.id] && formik.errors[question?.id] && (
                <Typography color="error" variant="caption">
                  {formik.errors[question?.id]}
                </Typography>
              )}
            </FormControl>
          </>
        );
      case 'select':
        return (
          <>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <FormControl
              fullWidth
              error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
            >
              <Select
                labelId={`${question.id}-label`}
                id={question.id}
                name={question.id}
                value={formik.values[question.id] || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required={question.required}
              >
                {question.options.map((option, index) => (
                  <MenuItem key={index} value={option} sx={{ fontSize: '0.875rem' }}>{option}</MenuItem>
                ))}
              </Select>
              {formik.touched[question?.id] && formik.errors[question?.id] && (
                <Typography color="error" variant="caption">
                  {formik.errors[question?.id]}
                </Typography>
              )}
            </FormControl>
          </>
        );
      case 'multi_select':
        return (
          <>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <FormControl
              fullWidth
              error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
            >
              <Select
                labelId={`${question.id}-label`}
                id={question.id}
                name={question.id}
                multiple
                value={formik.values[question.id] || []}
                onChange={(e) => {
                  formik.setFieldValue(question.id, e.target.value);
                }}
                onBlur={formik.handleBlur}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                size="small"
                sx={{
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    minHeight: '42px'
                  }
                }}
              >
                {question.options.map((option, index) => (
                  <MenuItem key={index} value={option} sx={{ fontSize: '0.875rem' }}>
                    <Checkbox checked={formik.values[question.id]?.indexOf(option) > -1} size="small" />
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched[question?.id] && formik.errors[question?.id] && (
                <Typography color="error" variant="caption">
                  {formik.errors[question?.id]}
                </Typography>
              )}
            </FormControl>
          </>
        );
      case 'number':
        return (
          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <TextField
              fullWidth
              id={question.id}
              name={question.id}
              type="number"
              value={formik.values[question.id] || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched[question.id] && Boolean(formik.errors[question.id])}
              helperText={formik.touched[question.id] && formik.errors[question.id]}
              required={question.required}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
              InputProps={{
                inputProps: {
                  min: question.min,
                  max: question.max,
                  step: question.step
                }
              }}
            />
          </Box>
        );
      case 'date':
        return (
          <>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "600",
                textAlign: "left",
                fontFamily: 'Outfit',
                fontSize: { xs: "16px", sm: "18px" },
                mb: "10px",
              }}
            >
              {questionNumber}. {question?.label}
              {question.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <FormControl
                fullWidth
                error={formik.touched[question?.id] && Boolean(formik.errors[question?.id])}
              >
                <DatePicker
                  value={formik.values[question.id]}
                  onChange={(date) => formik.setFieldValue(question.id, date)}
                  onBlur={() => formik.setFieldTouched(question.id, true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={question.required}
                      size="small"
                      sx={{
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        }
                      }}
                      error={formik.touched[question.id] && Boolean(formik.errors[question.id])}
                      helperText={formik.touched[question.id] && formik.errors[question.id]}
                    />
                  )}
                />
              </FormControl>
            </LocalizationProvider>
          </>
        );
      default:
        return (
          <Typography color="error">
            Unknown question type: {question.type}
          </Typography>
        );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
      }}>
        <InfinitySpin width="200" color="#3f51b5" />
      </Box>
    );
  }

  if (isSubmitted) {
    return <SurveySuccess survey_token={survey_token} navigate={navigate} />;
  }

  if (isVerify) {
    return <SurveyAlreadySubmitted survey_token={survey_token} navigate={navigate} />;
  }

  return (
    <Container maxWidth="md">
      <Card elevation={3} sx={{
        borderRadius: 3,
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          py: 4,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
        }}>
          <Typography variant="h4" component="h1" sx={{
            fontWeight: 700,
            letterSpacing: '0.5px'
          }}>
            {formConfig?.title}
          </Typography>
          <Typography variant="subtitle1" sx={{
            mt: 2,
            opacity: 0.9,
            maxWidth: '80%',
            mx: 'auto'
          }}>
            {formConfig?.description}
          </Typography>
        </Box>

        <Divider />

        <CardContent sx={{ p: 4 }}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexDirection: 'column',
              width: '100%'
            }}>
              {formConfig?.questions?.map((question, index) => (
                <Box key={index} sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  width: '100%'
                }}>
                  {renderQuestionField(question, index)}
                </Box>
              ))}
            </Box>
            <CustomSubmitButton
              fullWidth
              type="submit"
              size="large"
              btnTitle="Submit Survey"
              loading={isLoading}
              sx={{
                py: 1.5,
                marginTop: '10px',
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)'
                }
              }}
              disabled={!formik.isValid || isLoading}
            />
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

const SurveySuccess = ({ survey_token, navigate }) => {
  const theme = useTheme();

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'rgba(13, 18, 41, 1)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
        <Card elevation={3} sx={{
          borderRadius: 3,
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: 6 }}>
            <Lottie
              animationData={WELL_DONE}
              loop={true}
              style={{
                height: 200,
                margin: '0 auto'
              }}
            />
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 700,
              mt: 3,
              color: theme.palette.success.main
            }}>
              Thank You!
            </Typography>
            <Typography variant="body1" sx={{
              mt: 2,
              mb: 4,
              color: theme.palette.text.secondary,
              fontSize: '1.1rem'
            }}>
              Your survey has been submitted successfully. We appreciate your time and feedback!
            </Typography>

            <CustomSubmitButton
              variant="outlined"
              color="primary"
              size="large"
              btnTitle="Back to Response"
              onSubmit={() => navigate(`/response/${survey_token}`)}
              sx={{
                px: 4,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none'
              }}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

const SurveyAlreadySubmitted = ({ survey_token, navigate }) => {
  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'rgba(13, 18, 41, 1)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <Card sx={{
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        p: 4,
        borderRadius: 3,
        boxShadow: 3
      }}>
        <Box sx={{ width: 200, height: 200, mx: 'auto' }}>
          <Lottie animationData={WELL_DONE} loop={true} />
        </Box>

        <Typography variant="h5" component="h2" sx={{
          mt: 2,
          mb: 3,
          fontWeight: 600,
          color: 'text.primary'
        }}>
          Survey Already Submitted
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          You've already completed this survey. Thank you for your participation!
        </Typography>

        <CustomSubmitButton
          fullWidth
          variant="contained"
          color="primary"
          btnTitle="View Your Responses"
          onSubmit={() => navigate(`/response/${survey_token}`)}
          sx={{ mt: 2 }}
        />
      </Card>
    </Box>
  );
};

export default SurveyForm;